/**
 * frontend/src/lib/userStorage.js — Sprint 6A.1 item 2.
 *
 * Browser storage that belongs to ONE person (a pending recruit, referral/invite codes, a
 * pending claim, a queued chat message, per-session reward flags). It is cleared:
 *   - on SIGNED_OUT, and
 *   - when a DIFFERENT user signs in than the last one seen in this browser.
 * Intents a signed-out visitor stores right before signing up (the referral code from an
 * /invite link, "Recruit this creator", "Claim your profile") survive their own first sign-in,
 * because nobody was signed in before them. A pending recruit also expires after 24 hours, so a
 * stale click can never fire for whoever signs in days later.
 * Device preferences (sounddao_mute_sfx) are not user data and are kept.
 */

export const LAST_USER_KEY = 'sounddao_last_user_id';
export const RECRUIT_KEY = 'sounddao_recruit_creator_id';
export const RECRUIT_TTL_MS = 24 * 60 * 60 * 1000;

export const USER_LOCAL_KEYS = [
  RECRUIT_KEY,
  'sounddao_recruited_artist',
  'sounddao_referral_code',
  'sounddao_claim_artist_id',
  'sounddao_pending_chat_message',
  'sounddao_feed_welcomed',
  'sounddao_returning',
  'artist_invite_code',
];
export const USER_SESSION_KEYS = [
  'sounddao_login_reward_claimed',
  'sounddao_claim_redirected',
  'sounddao_scroll_to',
];
const KEEP = new Set(['sounddao_mute_sfx', LAST_USER_KEY]);

const safe = (fn, fallback = null) => { try { return fn(); } catch { return fallback; } };

/** Removes every per-user key (the listed ones plus any other sounddao_* key except device prefs). */
export function clearUserStorage() {
  for (const store of [safe(() => window.localStorage), safe(() => window.sessionStorage)]) {
    if (!store) continue;
    const keys = safe(() => Object.keys(store), []);
    for (const k of new Set([...USER_LOCAL_KEYS, ...USER_SESSION_KEYS, ...keys.filter((x) => x.startsWith('sounddao_'))])) {
      if (!KEEP.has(k)) safe(() => store.removeItem(k));
    }
  }
}

/** Call whenever a session's user id is known. Clears per-user storage if the user changed. */
export function noteSignedInUser(userId) {
  if (!userId) return;
  const last = safe(() => window.localStorage.getItem(LAST_USER_KEY));
  if (last && last !== userId) clearUserStorage();
  safe(() => window.localStorage.setItem(LAST_USER_KEY, userId));
}

/** Call on SIGNED_OUT. */
export function noteSignedOut() {
  clearUserStorage();
  safe(() => window.localStorage.removeItem(LAST_USER_KEY));
}

/** Store a signed-out visitor's "Recruit this creator" click (expires after 24 h). */
export function saveRecruitIntent(creatorUserId, now = Date.now()) {
  safe(() => window.localStorage.setItem(RECRUIT_KEY, JSON.stringify({ id: creatorUserId, at: now })));
}

/** Read AND remove the pending recruit; null if absent, malformed or older than 24 h. */
export function takeRecruitIntent(now = Date.now()) {
  const raw = safe(() => window.localStorage.getItem(RECRUIT_KEY));
  safe(() => window.localStorage.removeItem(RECRUIT_KEY));
  if (!raw) return null;
  const v = safe(() => JSON.parse(raw));
  if (!v || typeof v.id !== 'string' || typeof v.at !== 'number') return null;   // legacy / tampered
  return now - v.at <= RECRUIT_TTL_MS && now >= v.at ? v.id : null;
}
